import React, { useState } from 'react';
import { ButtonRounded, TextInput } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { validateFirstName, validateLastName } from '../../pages/verificationLandingPage/validators/Validators';
import InfoPopOver from '../infoPopOver/InfoPopOver';
import { FinApiVerificationAttempt } from '../../api/finapi/model/FinApiVerificationAttempt';
import {
  postFinApiVerificationAttempt,
  postFinApiVerificationAttemptLightRegisteredUser,
} from '../../api/finapi/FinApiVerificationAttemptApi';
import { toUrl as redirectToUrl } from '../../utils/redirect';
import finapiLogo from './finapi.svg';
import finapiLogoDark from './fin-api-dark.png';
import { useClientConfig } from '../../utils/clientConfigContext';
import { ApiResponseError } from '../../api/ApiResponseError';
import { trackEvent } from '../../tracking/tracking';

interface FinApiVerificationProps {
  onContentChange: (content: React.ReactNode) => void;
  onError: (error: string) => void;
  isDarkModeOn?: boolean;
}

const FinApiVerification: React.FC<FinApiVerificationProps> = ({ onContentChange, onError, isDarkModeOn }) => {
  const { clientId, baseUrl, userFirstName, userLastName, nameEditable, userId } = useClientConfig();
  const isFieldsDisabled = nameEditable === false;
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>(userFirstName || '');
  const [lastName, setLastName] = useState<string>(userLastName || '');
  const [invalidNameMessage, setInvalidNameMessage] = useState<string>('');
  const [invalidLastNameMessage, setInvalidLastNameMessage] = useState<string>('');
  const { t } = useTranslation();

  React.useEffect(() => {
    onContentChange(
      <div className='grid grid-flex gutter-horizontal-m margin-top-xxl'>
        <div className='grid-item one-half palm-one-whole palm-margin-bottom-l'>
          <TextInput
            data-testid='first-name-input'
            id='first-name'
            label={t('VerificationLandingPage.inputLabelFName')}
            placeholder=''
            mandatory={true}
            value={firstName}
            onChange={handleFirstInputChange}
            disabled={isFieldsDisabled}
            valid={invalidNameMessage.length === 0}
            captionText={invalidNameMessage}
          />
        </div>
        <div className='grid-item one-half palm-one-whole '>
          <TextInput
            data-testid='last-name-input'
            id='last-name'
            label={t('VerificationLandingPage.inputLabelLName')}
            placeholder=''
            value={lastName}
            mandatory={true}
            disabled={isFieldsDisabled}
            onChange={handleLastInputChange}
            valid={invalidLastNameMessage.length === 0}
            captionText={invalidLastNameMessage}
          />
        </div>
        <div className='grid grid-flex font-body-small-regular font-lightgray padding-left-m  margin-top-xxl palm-margin-top-xl  palm-margin-bottom-l'>
          {t('VerificationLandingPage.finapiHelperText')}
        </div>
      </div>,
    );
  }, [t, onContentChange, firstName, lastName, invalidNameMessage, invalidLastNameMessage]);

  const handleFirstInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value);
  };

  const handleLastInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
  };

  const validateUserInput = () => {
    const isFirstNameValid = validateFirstName(firstName);
    const isLastNameValid = validateLastName(lastName);
    setInvalidNameMessage(isFirstNameValid ? '' : t('VerificationLandingPage.firstNameError'));
    setInvalidLastNameMessage(isLastNameValid ? '' : t('VerificationLandingPage.lastNameError'));
    return isFirstNameValid && isLastNameValid;
  };

  const createRedirectionLink = (state: string): string => {
    const url = new URL(baseUrl);
    //URL.searchParams.set() doesn't replace in-place
    url.searchParams.set('tvs_state', state);
    return url.toString();
  };

  const addSessionIdToUrl = (urlString: string, sessionId: string): string => {
    try {
      const url = new URL(urlString);
      url.searchParams.set('tvs_session_id', sessionId);
      return url.toString();
    } catch (error) {
      if (urlString.includes('tvs_session_id=')) {
        return urlString.replace(/tvs_session_id=[^&]*/, `tvs_session_id=${sessionId}`);
      } else {
        return `${urlString}&tvs_session_id=${sessionId}`;
      }
    }
  };

  const onFinapiVerificationClick = () => {
    if (validateUserInput()) {
      trackEvent('is24.de.sicherheit.verifizierung.modal', 'verification_started', 'user', clientId, 'finapi');
      const finApiVerificationData: FinApiVerificationAttempt = {
        userDetails: {
          firstName: firstName,
          lastName: lastName,
        },
        successRedirectUrl: createRedirectionLink('verification_check_finapi'),
        errorRedirectUrl: createRedirectionLink('verification_failure_finapi_redirect'),
        abortRedirectUrl: createRedirectionLink('verification_failure_finapi_redirect'),
        requesterServiceId: clientId,
      };
      setIsRequestPending(true);
      const postVerificationAttempt = userId
        ? postFinApiVerificationAttemptLightRegisteredUser(finApiVerificationData, userId)
        : postFinApiVerificationAttempt(finApiVerificationData);

      postVerificationAttempt
        .then((response) => {
          setTimeout(() => {
            setIsRequestPending(false);
            const finapiRedirectUrl = new URL(response.redirectUrl);
            const redirectUrlValue = finapiRedirectUrl.searchParams.get('redirectUrl')!;
            const currentErrorUrlValue = finapiRedirectUrl.searchParams.get('errorRedirectUrl')!;
            const currentAbortUrlValue = finapiRedirectUrl.searchParams.get('abortRedirectUrl')!;
            const decodedRedirectUrl = decodeURIComponent(redirectUrlValue);
            const decodedErrorUrl = decodeURIComponent(currentErrorUrlValue);
            const decodedAbortUrl = decodeURIComponent(currentAbortUrlValue);

            // Properly add or update the tvs_session_id parameter
            const modifiedDecodedUrl = addSessionIdToUrl(decodedRedirectUrl, response.sessionId);
            const modifiedDecodedErrorUrl = addSessionIdToUrl(decodedErrorUrl, response.sessionId);
            const modifiedDecodedAbortUrl = addSessionIdToUrl(decodedAbortUrl, response.sessionId);

            finapiRedirectUrl.searchParams.set('redirectUrl', modifiedDecodedUrl);
            finapiRedirectUrl.searchParams.set('errorRedirectUrl', modifiedDecodedErrorUrl);
            finapiRedirectUrl.searchParams.set('abortRedirectUrl', modifiedDecodedAbortUrl);
            redirectToUrl(finapiRedirectUrl.toString());
          }, 400);
        })
        .catch((error: ApiResponseError) => {
          setIsRequestPending(false);
          if (error.responseStatus === 400) {
            onError('errors.finapi.bad.request');
          } else if (error.responseStatus === 429) {
            onError('errors.finapi.request.timeout');
          } else if (error.responseStatus === 401) {
            onError('errors.unauthorized');
          } else {
            onError('errors.default');
          }
        });
    }
  };

  return (
    <div className='modal-footer verification-modal-footer'>
      <div className='grid grid-flex'>
        <div className='grid-item grid-align-center one-half palm-one-whole'>
          <div className='grid grid-flex grid-align-center palm-padding-bottom-l'>
            <p className='margin-right-xs font-lightgray font-body-small-regular'>
              {t('VerificationLandingPage.footerText')}
            </p>
            <img
              src={isDarkModeOn ? finapiLogoDark : finapiLogo}
              alt='verification'
              className='finapi-footer-icon'
              height={16}
              width={49.48}
            />
            <InfoPopOver content={t(`InformationText.finapiPopOverContent`, { returnObjects: true })} />
          </div>
        </div>
        <div className='grid-item one-half palm-one-whole'>
          <div className='grid-flex grid-justify-end verification-footer-div palm-one-whole'>
            <ButtonRounded
              className='verification-footer-button'
              onClick={onFinapiVerificationClick}
              loading={isRequestPending}
              label={t('VerificationLandingPage.buttonText')}
            ></ButtonRounded>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinApiVerification;
